import { DrawerBody, DrawerFooter, Field, Input, InputProps, Text } from "@fluentui/react-components";

import LoadingButton from "../../../components/LoadingButton";
import OverlaySpinner from "../../../components/OverlaySpinner";

import { usePostOptionMutation } from "../api/CreateOption";
import { usePutOptionMutation } from "../api/UpdateOption";
import { useOptionDrawerStore } from "../store/OptionDrawerStore";
import { useCategoryTabStore } from "../store/CategoryTabStore";

import useOptionFormHook from "../hooks/useOptionFormHook";

import { useUtilitiesContext } from "../../../contexts/UtilitiesContext";
import { useCategoryContext } from "../context/CategoryContext";

import { Option } from "../data/Option";

export default function OptionForm() {
    const { isEdit, data, close } = useOptionDrawerStore();
    const { selectedCategory } = useCategoryTabStore();
    const { styles, nameId, name, setName, orderId, order, setOrder } = useOptionFormHook();
    const { showNotification } = useUtilitiesContext();
    const { refetchOptions } = useCategoryContext();

    const onError = (error: Error) => {
        showNotification({
            type: "error",
            title: "Error",
            message: error.message,
        });
    }

    const postOptionMutation = usePostOptionMutation(
        (data: Option) => {
            showNotification({
                type: "success",
                title: "Success",
                message: <Text>A request for approval to create option <b>{data.name}</b> has been sent. Please wait for approval.</Text>,
            });
            close();
            refetchOptions();
        },
        onError
    );

    const putOptionMutation = usePutOptionMutation(
        () => {
            showNotification({
                type: "success",
                title: "Success",
                message: <Text>A request for approval to update option <b>{data?.name}</b> has been sent. Please wait for approval.</Text>,
            });
            close();
            refetchOptions();
        },
        onError
    );

    const validateFields = () => {
        const requiredFields = [
            { value: name, message: "Category name is required" },
            { value: order, message: "Order is required" },
        ];

        for (const field of requiredFields) {
            if (!field.value) {
                showNotification({
                    type: "error",
                    title: "Error",
                    message: field.message,
                });
                return false;
            }
        }
        return true;
    };

    const onSubmit = () => {
        if (!validateFields()) return;

        if (!selectedCategory) {
            showNotification({
                type: "error",
                title: "Error",
                message: "Please select a category first.",
            });
            return;
        }

        if (isEdit && data) {
            putOptionMutation.mutate({
                categoryId: selectedCategory.id,
                id: data.id,
                name: name,
                order: order,
            });
        }
        else {
            postOptionMutation.mutate({
                categoryId: selectedCategory.id,
                name: name,
                order: order,
            });
        }
    }

    const handleInputChange: InputProps["onChange"] = (_, data) => {
        const value = parseInt(data.value, 10);
        if (value < 0) {
            // setOrder(0);
            return;
        }
        if (value > 999) {
            // setOrder(max);
            return;
        }
        setOrder(value);
    };

    const isLoading = isEdit ? putOptionMutation.isPending : postOptionMutation.isPending;
    const loadingLabel = isEdit ? "Updating..." : "Creating...";
    const buttonText = isEdit ? "Update" : "Create";

    return (
        <>
            <OverlaySpinner open={isLoading} label={loadingLabel} />

            <DrawerBody className={styles.drawerBody}>
                <div className={styles.inputWrapper}>
                    <Field label="Option name" required>
                        <Input
                            id={nameId}
                            type="text"
                            value={name}
                            maxLength={50}
                            onChange={(_, data) => setName(data.value)}
                        />
                    </Field>

                    <Field label="Order" required>
                        <Input
                            id={orderId}
                            type="number"
                            value={order.toString()}
                            min={1}
                            max={999}
                            onChange={handleInputChange}
                        />
                    </Field>
                </div>
            </DrawerBody>

            <DrawerFooter>
                <LoadingButton
                    text={buttonText}
                    disabled={isLoading}
                    loading={isLoading}
                    onClick={onSubmit}
                    appearance="primary"
                />
            </DrawerFooter>
        </>
    );
}